import { useEffect, useRef, useState } from "react";
import client from "@/lib/glossyApi";
import { getFeatures } from "@/lib/features";
import { Headphones, Play, Pause, Square } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ListenButton({ slug, accent }) {
  const [state, setState] = useState("idle"); // idle | loading | ready
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const [visible, setVisible] = useState(true);
  const audioRef = useRef(null);
  const slugRef = useRef(slug);

  useEffect(() => { getFeatures().then((f) => setVisible(f.listen !== false)); }, []);

  // New article or leaving the page: silence and release the old narration.
  useEffect(() => {
    slugRef.current = slug;
    setState("idle"); setPlaying(false); setStarted(false);
    return () => {
      const el = audioRef.current;
      if (el) { el.pause(); el.src = ""; audioRef.current = null; }
    };
  }, [slug]);

  const toggle = async () => {
    const el = audioRef.current;
    if (state === "ready" && el) {
      if (el.paused) el.play();
      else el.pause();
      return;
    }
    setState("loading");
    try {
      const { data } = await client.post(`/articles/${slug}/audio`);
      const a = new Audio(`${API}/files/${data.audio}`);
      a.onplay = () => { setPlaying(true); setStarted(true); };
      a.onpause = () => setPlaying(false);
      a.onended = () => { setPlaying(false); setStarted(false); a.currentTime = 0; };
      if (slugRef.current !== slug) { a.src = ""; return; }
      audioRef.current = a;
      setState("ready");
      await a.play().catch(() => {});
    } catch {
      setState("idle");
    }
  };

  const stop = () => {
    const el = audioRef.current;
    if (!el) return;
    el.pause();
    el.currentTime = 0;
    setStarted(false);
  };

  if (!visible) return null;
  return (
    <div className="inline-flex items-stretch gap-2">
      <button
        onClick={toggle}
        disabled={state === "loading"}
        className="inline-flex items-center gap-2.5 border border-ink/25 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] hover:border-ink transition-colors disabled:opacity-60"
        style={accent ? { borderColor: playing ? accent : undefined } : undefined}
        data-testid="listen-button"
      >
        {state === "loading" ? (
          <><Headphones size={14} className="animate-pulse" /> Preparing narration…</>
        ) : playing ? (
          <><Pause size={14} /> Pause</>
        ) : started ? (
          <><Play size={14} /> Resume</>
        ) : (
          <><Headphones size={14} /> Listen to this story</>
        )}
      </button>
      {state === "ready" && started && (
        <button
          onClick={stop}
          className="inline-flex items-center border border-ink/25 px-3.5 hover:border-ink transition-colors"
          aria-label="Stop narration"
          data-testid="listen-stop-button"
        >
          <Square size={12} />
        </button>
      )}
    </div>
  );
}
